import React from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { Spinner } from 'reactstrap';
import { FiClock, FiCoffee, FiTrendingUp } from 'react-icons/fi';
import { Accordion } from '@/components/Accordion';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { SessionSourceTags } from '@/components/SessionSourceTags';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import {
  getScreenshotPreviewSrc,
  getScreenshotRevealKey,
} from '@/utils/screenshotPrivacy';
import { SessionStatItemComponent as SessionStatItem } from './SessionStatItem.component';
import styles from './SessionCard.module.scss';

const BULLET_PALETTE = ['blue', 'orange', 'success', 'purple'];
const MAX_VISIBLE_THUMBNAILS = 4;
const SCREENSHOT_FALLBACK_SRC =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";

const ScreenshotThumbnails = ({
  screenshots,
  isLoading,
  selectedTimezone,
  onOpenScreenshotModal,
}) => {
  if (isLoading) {
    return (
      <div className={styles.screenshotsLoading}>
        <Spinner size="sm" color="primary" />
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          Loading screenshots...
        </Text>
      </div>
    );
  }

  if (!screenshots?.length) {
    return (
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
        className={styles.noScreenshots}
      >
        No screenshots available for this session.
      </Text>
    );
  }

  const visible = screenshots.slice(0, MAX_VISIBLE_THUMBNAILS);
  const remaining = screenshots.length - visible.length;

  return (
    <div className={styles.screenshotsRow}>
      {visible.map((screenshot, idx) => {
        const revealKey = getScreenshotRevealKey(screenshot);
        return (
          <div
            key={revealKey || idx}
            className={styles.screenshotItem}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenScreenshotModal(idx, screenshots);
              }
            }}
            onClick={() => onOpenScreenshotModal(idx, screenshots)}
          >
            <img
              src={getScreenshotPreviewSrc(screenshot)}
              alt={`Screenshot ${idx + 1}`}
              className={styles.image}
              loading="lazy"
              onError={(e) => {
                e.target.src = SCREENSHOT_FALLBACK_SRC;
                e.target.onerror = null;
              }}
            />
            <span className={styles.timeStamp}>
              {screenshot.timestamp
                ? moment(screenshot.timestamp)
                    .tz(selectedTimezone)
                    .format('hh:mm A')
                : null}
            </span>
          </div>
        );
      })}
      {remaining > 0 && (
        <button
          type="button"
          className={styles.moreTile}
          onClick={() =>
            onOpenScreenshotModal(MAX_VISIBLE_THUMBNAILS, screenshots)
          }
        >
          <Text
            variant={textVariantsEnum.Text_lg}
            fontWeight={fontWeightEnum.Semibold}
            color="indigo-600"
          >
            +{remaining}
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
            color="indigo-600"
          >
            more
          </Text>
        </button>
      )}
    </div>
  );
};

const SessionStatsRow = ({
  boxed,
  duration,
  idleTime,
  idlePercent,
  activityTime,
  activityPercent,
}) => (
  <div
    className={classNames(styles.statsGroup, {
      [styles.statsGroup_boxed]: boxed,
    })}
  >
    <SessionStatItem
      icon={<FiClock size={16} color="var(--indigo-600)" />}
      iconBg="indigo-100"
      label="Duration"
      value={duration}
    />
    <SessionStatItem
      icon={<FiCoffee size={16} color="var(--orange-600)" />}
      iconBg="orange-100"
      label="Idle Time"
      value={idleTime}
      percentText={idlePercent}
      percentTone="warning"
    />
    <SessionStatItem
      icon={<FiTrendingUp size={16} color="var(--success-600)" />}
      iconBg="success-100"
      label="Activity Time"
      value={activityTime}
      percentText={activityPercent}
      percentTone="success"
    />
  </div>
);

export const SessionCardComponent = ({
  dayLabel,
  dayKey,
  session,
  selectedTimezone,
  screenshots,
  isLoadingScreenshots,
  isOpenOnStart,
  isExpanded,
  colorIndex = 0,
  onOpenChange,
  onOpenScreenshotModal,
}) => {
  const startTime = moment(session.start)
    .tz(selectedTimezone)
    .format('hh:mm A');
  const endTime = session.end
    ? moment(session.end).tz(selectedTimezone).format('hh:mm A')
    : 'Ongoing';
  const duration = formatTimeFromSeconds(session.durationSec);
  const { displayIdle, idlePercent } = normalizeIdleForDisplay(
    session.durationSec,
    session.idleSec
  );
  const activityTime = formatTimeFromSeconds(
    Math.max(0, (session.durationSec || 0) - displayIdle)
  );
  const bulletColor =
    BULLET_PALETTE[
      ((colorIndex % BULLET_PALETTE.length) + BULLET_PALETTE.length) %
        BULLET_PALETTE.length
    ];
  const isToday = moment().format('YYYY-MM-DD') === dayKey;
  const isYesterday =
    moment().subtract(1, 'day').format('YYYY-MM-DD') === dayKey;
  const dayLabelPrefix = isToday ? 'Today, ' : isYesterday ? 'Yesterday, ' : '';

  return (
    <div className={styles.sessionCard}>
      <Accordion
        isOpenOnStart={isOpenOnStart}
        onOpenChange={onOpenChange}
        headingClass={styles.sessionHeadingWrapper}
        heading={
          <div className={styles.sessionHeading}>
            <div className={styles.titleRow}>
              <div className={styles.titleLeft}>
                <span className={styles.bullet} data-color={bulletColor} />
                <Text
                  variant={textVariantsEnum.Text_md}
                  fontWeight={fontWeightEnum.Semibold}
                  color={isToday ? 'blue-600' : 'gray-900'}
                >
                  {dayLabelPrefix}
                  {dayLabel}
                </Text>
              </div>
              {!isExpanded && (
                <SessionStatsRow
                  duration={duration}
                  idleTime={formatTimeFromSeconds(displayIdle)}
                  idlePercent={formatPercent(idlePercent)}
                  activityTime={activityTime}
                  activityPercent={formatPercent(session.activityPct)}
                />
              )}
            </div>
            <div className={styles.timeRangeGroup}>
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Semibold}
                color="gray-900"
              >
                {startTime} to {endTime}
              </Text>
              <Text
                variant={textVariantsEnum.Text_xxs}
                fontWeight={fontWeightEnum.Normal}
                color="gray-400"
              >
                (Duration: {duration})
              </Text>
              <SessionSourceTags
                pastData={session?.pastData}
                typeLog={session?.typeLog}
              />
            </div>
          </div>
        }
      >
        <div className={styles.content}>
          {isExpanded && (
            <SessionStatsRow
              boxed
              duration={duration}
              idleTime={formatTimeFromSeconds(displayIdle)}
              idlePercent={formatPercent(idlePercent)}
              activityTime={activityTime}
              activityPercent={formatPercent(session.activityPct)}
            />
          )}
          <ScreenshotThumbnails
            screenshots={screenshots}
            isLoading={isLoadingScreenshots}
            selectedTimezone={selectedTimezone}
            onOpenScreenshotModal={onOpenScreenshotModal}
          />
        </div>
      </Accordion>
    </div>
  );
};

SessionCardComponent.displayName = 'SessionCard';
